import seoUrlData from './seo-url-data.json';

const { blog, services, caseStudies } = seoUrlData;

/** Max slug length for new blog posts (SEO best practice). */
export const BLOG_SLUG_MAX_LENGTH = 75;

export const BLOG_CANONICAL_SLUG_BY_ID = blog.canonicalSlugById;
export const BLOG_LEGACY_SLUG_REDIRECTS = blog.legacyRedirects;

export const SERVICE_CANONICAL_SLUGS = services.canonicalSlugs;
export const SERVICE_LEGACY_SLUG_REDIRECTS = services.legacyRedirects;

export const CASE_STUDY_CANONICAL_SLUGS = caseStudies.canonicalSlugs;
export const CASE_STUDY_LEGACY_SLUG_REDIRECTS = caseStudies.legacyRedirects;

export function blogPath(slug) {
  if (!slug) return '/blog';
  return `/blog/${slug}`;
}

export function servicePath(slug) {
  const canonical = resolveServiceCanonicalSlug(slug);
  if (!canonical) return '/services';
  return `/services/${canonical}`;
}

export function caseStudyPath(slug) {
  const canonical = resolveCaseStudyCanonicalSlug(slug);
  if (!canonical) return '/case-studies';
  return `/case-studies/${canonical}`;
}

export function resolveBlogLegacyRedirect(slug) {
  if (!slug) return null;
  return BLOG_LEGACY_SLUG_REDIRECTS[slug] ?? null;
}

export function resolveServiceCanonicalSlug(slug) {
  if (!slug) return null;
  const key = String(slug).trim().toLowerCase();
  if (SERVICE_LEGACY_SLUG_REDIRECTS[key]) return SERVICE_LEGACY_SLUG_REDIRECTS[key];
  if (SERVICE_CANONICAL_SLUGS.includes(key)) return key;
  return null;
}

export function resolveCaseStudyCanonicalSlug(slug) {
  if (!slug) return null;
  const key = String(slug).trim().toLowerCase();
  if (CASE_STUDY_LEGACY_SLUG_REDIRECTS[key]) return CASE_STUDY_LEGACY_SLUG_REDIRECTS[key];
  if (CASE_STUDY_CANONICAL_SLUGS.includes(key)) return key;
  return null;
}

/** Prefer SEO registry canonical slug, then CMS explicit slug, then title-derived. */
export function resolveBlogCanonicalSlug(post) {
  if (!post) return '';
  const fromRegistry = BLOG_CANONICAL_SLUG_BY_ID[String(post.id)];
  if (fromRegistry) return fromRegistry;
  const explicit = slugifyForUrl(post.slug);
  if (explicit) return explicit;
  return slugifyForUrl(post.title) || `post-${post.id}`;
}

export function slugifyForUrl(text) {
  return String(text || '')
    .trim()
    .toLowerCase()
    .replace(/[''"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, BLOG_SLUG_MAX_LENGTH)
    .replace(/^-+|-+$/g, '');
}

/** Index posts by canonical slug, numeric id, and legacy slug aliases. */
export function buildBlogPostsSlugIndex(posts) {
  const map = {};
  const addAlias = (key, post) => {
    if (!key) return;
    map[key] = post;
    if (key.endsWith('-')) map[key.slice(0, -1)] = post;
  };
  (posts || []).forEach((post) => {
    if (!post?.slug) return;
    addAlias(post.slug, post);
    addAlias(String(post.id), post);
    Object.entries(BLOG_LEGACY_SLUG_REDIRECTS).forEach(([legacy, canonical]) => {
      if (canonical === post.slug) addAlias(legacy, post);
    });
  });
  return map;
}

/** Flat list of { source, destination } for Vercel 301 redirects. */
export function buildSeoRedirectRules() {
  const rules = [];

  Object.entries(BLOG_LEGACY_SLUG_REDIRECTS).forEach(([from, to]) => {
    rules.push({ source: `/blog/${from}`, destination: `/blog/${to}` });
  });

  Object.entries(SERVICE_LEGACY_SLUG_REDIRECTS).forEach(([from, to]) => {
    rules.push({ source: `/services/${from}`, destination: `/services/${to}` });
  });

  Object.entries(CASE_STUDY_LEGACY_SLUG_REDIRECTS).forEach(([from, to]) => {
    rules.push({ source: `/case-studies/${from}`, destination: `/case-studies/${to}` });
  });

  return rules;
}
